export type WorkflowNodeType =
    | 'trigger'
    | 'transform'
    | 'httpRequest'
    | 'lookupMedia'
    | 'branch'
    | 'emitCodes';

/** Trigger types an activation workflow can be keyed to. */
export type WorkflowTriggerType =
    | 'qrScan'
    | 'event'
    | 'share'
    | 'mediaUploaded';

export interface TriggerData {
    triggerType?: string;
    inputName?: string;
    parseJson?: boolean;
    eventName?: string;
}

export interface TransformAssignment {
    key: string;
    expression: string;
}

export interface TransformData {
    assignments: TransformAssignment[];
}

export interface HttpHeader {
    key: string;
    value: string;
}

export interface HttpRequestData {
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    url: string;
    headers?: HttpHeader[];
    bodyMode?: 'none' | 'json' | 'raw' | 'multipart';
    body?: string;
    parts?: MultipartPart[];
    timeoutMs?: number;
}

export interface MultipartPart {
    name: string;
    kind: 'field' | 'file';
    value: string;
    filename?: string;
    contentType?: string;
}

export interface LookupMediaData {
    shareIdentifiers: string;
}

export interface BranchCondition {
    id: string;
    label?: string;
    expression: string;
}

export interface BranchData {
    conditions: BranchCondition[];
}

export interface EmitCodeField {
    key: string;
    valueExpression: string;
}

export interface EmitCode {
    action: string;
    fields: EmitCodeField[];
}

export interface EmitCodesData {
    codes: EmitCode[];
}

export type WorkflowNodeConfig =
    | TriggerData
    | TransformData
    | HttpRequestData
    | LookupMediaData
    | BranchData
    | EmitCodesData;

export interface WorkflowNode {
    id: string;
    type: WorkflowNodeType | string;
    key?: string;
    label?: string;
    position?: { x: number; y: number };
    data: WorkflowNodeConfig;
}

export interface WorkflowEdge {
    id: string;
    source: string;
    target: string;
    sourceHandle?: string;
}

export interface WorkflowDefinition {
    nodes: WorkflowNode[];
    edges: WorkflowEdge[];
    responseType?: string;
}

export interface CatalogTrigger {
    type: string;
    label: string;
}

export interface CatalogStep {
    type: string;
    label: string;
    category: 'action' | 'logic' | 'output';
}

export interface CatalogResponse {
    type: string;
    label: string;
}

export interface WorkflowCatalog {
    triggers: CatalogTrigger[];
    steps: CatalogStep[];
    responses: CatalogResponse[];
    defaultTriggerType: string;
    defaultResponseType: string;
}

/** Step types that have a dedicated config panel + node renderer in the editor. */
export const KNOWN_STEP_TYPES = [
    'transform',
    'httpRequest',
    'lookupMedia',
    'branch',
    'emitCodes',
];

export interface NodeTrace {
    nodeId: string;
    nodeKey: string;
    type: WorkflowNodeType;
    output?: unknown;
    branchTaken?: string;
    error?: string;
}

export interface TestRunResult {
    success: boolean;
    output?: { action: string; data: Record<string, string> }[];
    context?: Record<string, unknown>;
    trace?: NodeTrace[];
    error?: string;
    details?: string[];
}

export type WorkflowRunStatus =
    | 'pending'
    | 'running'
    | 'completed'
    | 'failed';

export interface WorkflowRunSummary {
    id: string;
    status: WorkflowRunStatus;
    error: string | null;
    inputPreview: string | null;
    createdAt: string;
    completedAt: string | null;
}

export interface WorkflowRunDetail {
    id: string;
    status: WorkflowRunStatus;
    input: string | null;
    output: unknown;
    trace: NodeTrace[];
    error: string | null;
    createdAt: string;
    completedAt: string | null;
}

/** The clickerbooth action names an emitCodes node can produce. */
export const CLICKERBOOTH_ACTIONS = [
    'navigate',
    'reset',
    'setValue',
    'appendValue',
    'share',
    'print',
    'searchShares',
    'setNextProcessingFlow',
    'capture',
];
